import React, { useState, useEffect, useCallback } from 'react';
import { useAlert } from '../../../../contexts/AlertContext';
import { useAuthStore } from '../../../../store/authStore';
import {
  Users,
  CheckCircle,
  ClipboardList,
  Ticket,
  Search,
  RefreshCw,
  UserPlus,
  Shield,
  Megaphone,
  CreditCard,
  User,
  Edit2,
  X
} from 'lucide-react';
import './UserManagementPanel.css';

const UserManagementPanel = () => {
  const currentUser = useAuthStore((state) => state.user);
  const { showAlert } = useAlert();
  const [users, setUsers] = useState([]);
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [showEditModal, setShowEditModal] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [userStats, setUserStats] = useState(null);
  const currentUserRoles = (currentUser?.roles || []).map((r) => r.toUpperCase());
  const isPlannerNonAdmin = currentUserRoles.includes('PLANNER') && !currentUserRoles.includes('ADMIN');

  const usersPerPage = 10;

  const loadUsers = useCallback(async () => {
    try {
      setLoading(true);

      const token = localStorage.getItem('authToken');
      const params = new URLSearchParams({
        page: (currentPage - 1).toString(),
        size: usersPerPage.toString(),
        sortBy: 'createdAt',
        sortDirection: 'desc'
      });

      if (searchQuery.trim()) {
        params.append('search', searchQuery.trim());
      }
      if (roleFilter && roleFilter !== 'all') {
        params.append('role', roleFilter.toUpperCase());
      }
      if (statusFilter && statusFilter !== 'all') {
        params.append('isActive', statusFilter === 'active');
      }

      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/users?${params}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch users: ${response.status}`);
      }

      const result = await response.json();

      if (result.success && result.data) {
        const transformedUsers = result.data.content.map((user) => ({
          id: user.id,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          isActive: (user.isActive ?? user.active ?? false),
          status: (user.isActive ?? user.active ?? false) ? 'active' : 'inactive',
          createdAt: user.createdAt,
          lastLogin: user.lastLoginAt,
          roles: (user.roles || []).map((role) => role.toUpperCase()),
          isAdmin: user.roles?.includes('ADMIN'),
          isPlanner: user.roles?.includes('PLANNER'),
          isPromoter: user.roles?.includes('PROMOTER'),
          isStaff: user.roles?.includes('STAFF'),
          isSaleAgent: user.roles?.includes('SALE_AGENT'),
          isBuyer: user.roles?.includes('BUYER') || user.roles?.includes('CUSTOMER')
        }));

        setUsers(transformedUsers);
        setTotalPages(result.data.totalPages || 0);
        setTotalElements(result.data.totalElements || 0);
      } else {
        setUsers([]);
        setTotalPages(0);
        setTotalElements(0);
      }
    } catch (error) {
      console.error('Error loading users:', error);
      setUsers([]);
      setTotalPages(0);
      setTotalElements(0);
    } finally {
      setLoading(false);
    }
  }, [currentPage, roleFilter, searchQuery, statusFilter]);

  const loadUserStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/users/stats`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Cache-Control': 'no-cache'
          }
        }
      );

      if (!response.ok) {
        throw new Error(`Failed to fetch user stats: ${response.status}`);
      }

      const result = await response.json();
      if (result.success && result.data) {
        setUserStats(result.data);
      } else {
        setUserStats(null);
      }
    } catch (error) {
      console.error('Error loading user stats:', error);
      setUserStats(null);
    }
  }, []);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  useEffect(() => {
    loadUserStats();
  }, [loadUserStats]);

  const handleEditSave = async (userData) => {
    try {
      const token = localStorage.getItem('authToken');
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8080/api'}/admin/users/${userData.id}`,
        {
          method: 'PUT',
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            firstName: userData.firstName,
            lastName: userData.lastName,
            email: userData.email,
            isActive: userData.status === 'active',
            roles: userData.roles || []
          })
        }
      );

      const responseBody = await response.json().catch(() => null);

      if (response.ok) {
        await loadUsers();
        await loadUserStats();
        setShowEditModal(false);
        setEditingUser(null);
        showAlert({ title: 'Success', message: 'User updated successfully', type: 'success' });
      } else {
        const backendMessage = responseBody?.message || responseBody?.error?.message;
        throw new Error(backendMessage || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      showAlert({
        title: 'Save failed',
        message: `Failed to update user: ${error.message}`,
        type: 'error'
      });
    }
  };

  const updateRolesList = (currentRoles, role, isChecked) => {
    if (isChecked) {
      return currentRoles.includes(role) ? currentRoles : [...currentRoles, role];
    }
    return currentRoles.filter((r) => r !== role);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    try {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const formatStatValue = (value) => {
    const numericValue = Number(value ?? 0);
    return Number.isNaN(numericValue) ? '0' : numericValue.toLocaleString();
  };

  const getRoleIcon = (role) => {
    switch (role) {
      case 'ADMIN': return <Shield size={12} />;
      case 'PLANNER': return <ClipboardList size={12} />;
      case 'PROMOTER': return <Megaphone size={12} />;
      case 'SALE_AGENT': return <CreditCard size={12} />;
      default: return <User size={12} />;
    }
  };

  return (
    <div className="admin-users-panel">
      {userStats && (
        <div className="users-stats-row">
          <div className="users-stat-card" style={{ '--stat-accent': '#3b82f6' }}>
            <div className="stat-heading">
              <div className="stat-icon-wrapper"><Users size={20} /></div>
              <span className="stat-label">Total Users</span>
            </div>
            <div className="stat-value">{formatStatValue(userStats.totalUsers)}</div>
            <div className="stat-subtext">+{formatStatValue(userStats.newThisMonth)} this month</div>
          </div>
          <div className="users-stat-card" style={{ '--stat-accent': '#10b981' }}>
            <div className="stat-heading">
              <div className="stat-icon-wrapper"><CheckCircle size={20} /></div>
              <span className="stat-label">Active Users</span>
            </div>
            <div className="stat-value">{formatStatValue(userStats.activeUsers)}</div>
            <div className="stat-subtext">
              {userStats.totalUsers ? ((userStats.activeUsers / userStats.totalUsers) * 100).toFixed(1) : 0}% active
            </div>
          </div>
          <div className="users-stat-card" style={{ '--stat-accent': '#a855f7' }}>
            <div className="stat-heading">
              <div className="stat-icon-wrapper"><ClipboardList size={20} /></div>
              <span className="stat-label">Planners</span>
            </div>
            <div className="stat-value">{formatStatValue(userStats.plannerUsers)}</div>
            <div className="stat-subtext">Content creators</div>
          </div>
          <div className="users-stat-card" style={{ '--stat-accent': '#f59e0b' }}>
            <div className="stat-heading">
              <div className="stat-icon-wrapper"><Ticket size={20} /></div>
              <span className="stat-label">Buyers</span>
            </div>
            <div className="stat-value">{formatStatValue(userStats.buyerUsers)}</div>
            <div className="stat-subtext">Ticket purchasers</div>
          </div>
        </div>
      )}

      <div className="users-panel-shell">
        <div className="users-panel-header">
          <div className="users-heading">
            <Users size={24} className="text-blue-500" />
            <h2>User Management</h2>
          </div>
          <div className="users-panel-actions">
            <button className="users-control-btn secondary" onClick={loadUsers}>
              <RefreshCw size={16} /> Refresh
            </button>
            <button className="users-control-btn primary">
              <UserPlus size={16} /> Invite User
            </button>
          </div>
        </div>

        <div className="users-filter-bar">
          <div className="users-search-wrapper">
            <Search className="users-search-icon" size={16} />
            <input
              type="text"
              className="users-search-input"
              placeholder="Search users by name or email..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <div style={{ position: 'relative' }}>
            <select
              className="users-filter-select"
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
            >
              <option value="all">All Roles</option>
              <option value="admin">Admin</option>
              <option value="planner">Planner</option>
              <option value="promoter">Promoter</option>
              <option value="sale-agent">Sale Agent</option>
              <option value="staff">Staff</option>
              <option value="buyer">Buyer</option>
            </select>
          </div>
          <div style={{ position: 'relative' }}>
            <select
              className="users-filter-select"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
              <option value="pending">Pending</option>
            </select>
          </div>
        </div>

        {users.length === 0 && !loading ? (
          <div className="users-empty">
            <Users size={48} style={{ opacity: 0.3 }} />
            <h3>No users found</h3>
            <p>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="users-table-container">
            <table className="users-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Roles</th>
                  <th>Status</th>
                  <th>Joined</th>
                  <th>Last Login</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id}>
                    <td data-label="User">
                      <div className="user-cell">
                        <div className="user-avatar-small">
                          {(user.firstName?.[0] || 'U')}{(user.lastName?.[0] || '')}
                        </div>
                        <div className="user-details">
                          <h4>{[user.firstName, user.lastName].join(' ')}</h4>
                          <span className="user-email">{user.email}</span>
                        </div>
                      </div>
                    </td>
                    <td data-label="Roles">
                      {(user.roles && user.roles.length > 0 ? user.roles : ['BUYER']).map(role => (
                        <span key={role} className={`role-badge ${role.toLowerCase()}`}>
                          {getRoleIcon(role)} {role.replace('_', ' ')}
                        </span>
                      ))}
                    </td>
                    <td data-label="Status">
                      <span className={`status-pill ${user.status}`}>
                        {user.status === 'active' ? <CheckCircle size={12} /> : <X size={12} />}
                        {user.status}
                      </span>
                    </td>
                    <td data-label="Joined">{formatDate(user.createdAt)}</td>
                    <td data-label="Last Login">{formatDate(user.lastLogin)}</td>
                    <td data-label="Actions">
                      <button
                        className="action-btn-icon"
                        onClick={() => {
                          setEditingUser(user);
                          setShowEditModal(true);
                        }}
                      >
                        <Edit2 size={16} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="pagination-controls">
          <span className="pagination-info">
            Showing {(currentPage - 1) * usersPerPage + 1}-{Math.min(currentPage * usersPerPage, totalElements)} of {totalElements}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              className="page-btn"
              disabled={currentPage === 1}
              onClick={() => setCurrentPage(p => p - 1)}
            >
              Prev
            </button>
            <button
              className="page-btn"
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage(p => p + 1)}
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {showEditModal && editingUser && (
        <div className="user-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="user-modal" onClick={e => e.stopPropagation()}>
            <div className="user-modal-header">
              <h3>Edit User</h3>
              <button className="close-modal-btn" onClick={() => setShowEditModal(false)}>
                <X size={20} />
              </button>
            </div>

            <div className="user-modal-body">
              <div className="modal-form-group">
                <label className="modal-label">First Name</label>
                <input
                  type="text"
                  className="modal-input"
                  value={editingUser.firstName || ''}
                  disabled={isPlannerNonAdmin}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, firstName: e.target.value }))}
                />
              </div>
              <div className="modal-form-group">
                <label className="modal-label">Last Name</label>
                <input
                  type="text"
                  className="modal-input"
                  value={editingUser.lastName || ''}
                  disabled={isPlannerNonAdmin}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, lastName: e.target.value }))}
                />
              </div>
              <div className="modal-form-group">
                <label className="modal-label">Email</label>
                <input
                  type="email"
                  className="modal-input"
                  value={editingUser.email || ''}
                  disabled={isPlannerNonAdmin}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, email: e.target.value }))}
                />
              </div>

              <div className="modal-form-group">
                <label className="modal-label">Roles</label>
                <div className="role-checkbox-grid">
                  {[
                    { key: 'ADMIN', label: 'Admin' },
                    { key: 'PLANNER', label: 'Planner' },
                    { key: 'PROMOTER', label: 'Promoter' },
                    { key: 'SALE_AGENT', label: 'Sale Agent' },
                    { key: 'STAFF', label: 'Staff' },
                    { key: 'BUYER', label: 'Buyer' }
                  ].map(role => (
                    <label key={role.key} className="role-checkbox-item">
                      <input
                        type="checkbox"
                        disabled={isPlannerNonAdmin}
                        checked={editingUser.roles?.includes(role.key) || false}
                        onChange={(e) => setEditingUser(prev => ({
                          ...prev,
                          roles: updateRolesList(prev.roles || [], role.key, e.target.checked)
                        }))}
                      />
                      {role.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="modal-form-group">
                <label className="modal-label">Status</label>
                <select
                  className="modal-input"
                  value={editingUser.status}
                  disabled={isPlannerNonAdmin}
                  onChange={(e) => setEditingUser(prev => ({ ...prev, status: e.target.value }))}
                >
                  <option value="active">Active</option>
                  <option value="inactive">Inactive</option>
                </select>
              </div>
            </div>

            <div className="user-modal-actions">
              <button className="users-control-btn secondary" onClick={() => setShowEditModal(false)}>
                Cancel
              </button>
              <button
                className="users-control-btn primary"
                onClick={() => handleEditSave(editingUser)}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagementPanel;
